import {
  useEffect,
  useState,
  useRef,
  ReactNode,
} from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { AuthContext } from "@/contexts/authContextDef";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  // Deduplicate concurrent checkAdmin calls for the same userId.
  // Both getSession() and onAuthStateChange fire on mount; the ref ensures only
  // one role query is in-flight at a time for a given user.
  const adminCheckIdRef = useRef<string | null>(null);

  const checkAdmin = async () => {
    // Resolve admin status from the JWT-verified user (getClaims) rather than
    // the session object, which is read straight from storage and may not be
    // authentic. A forged or expired token yields no verified id -> not admin.
    const { data: claimsData } = await supabase.auth.getClaims();
    const verifiedUserId = claimsData?.claims?.sub;
    if (!verifiedUserId) {
      adminCheckIdRef.current = null;
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    if (adminCheckIdRef.current === verifiedUserId) {
      return;
    }
    adminCheckIdRef.current = verifiedUserId;

    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", verifiedUserId)
        .eq("role", "admin")
        .maybeSingle();

      if (error) {
        setIsAdmin(false);
      } else {
        setIsAdmin(!!data);
      }
    } catch {
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    // Fast-path: clear loading state on mount if there is definitively no local session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        checkAdmin();
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        await checkAdmin();
      } else {
        adminCheckIdRef.current = null;
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) return { error };

    setSession(data.session);
    setUser(data.user);
    if (data.user) {
      await checkAdmin();
    }
    return { error: null };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { error: error ? new Error(error.message) : null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    adminCheckIdRef.current = null;
  };

  return (
    <AuthContext
      value={{ user, session, isAdmin, loading, signIn, signUp, signOut }}
    >
      {children}
    </AuthContext>
  );
}
